import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Link,
  Divider,
  InputAdornment,
  IconButton,
  Paper
} from '@mui/material';
import { Visibility, VisibilityOff, Email, Lock, Person } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import UserAuthService from '../../services/userAuthService';
import BotDetectionService from '../../services/botDetectionService';
import { getCleanErrorMessage } from '../../utils/errorUtils';

interface RegisterFormProps {
  onSuccess: () => void;
  onSwitchToLogin: () => void;
  onSubmit?: (formData: any) => void; // For bot detection wrapper
}

const RegisterForm: React.FC<RegisterFormProps> = ({ onSuccess, onSwitchToLogin, onSubmit }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    displayName: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [botDetected, setBotDetected] = useState(false);
  const botDetectionService = BotDetectionService.getInstance();
  const startTime = useRef<number>(Date.now());
  const typingStartTime = useRef<number>(0);
  const lastTypingTime = useRef<number>(0);

  // Track typing behavior for bot detection
  const handleTyping = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const now = Date.now();
    const value = e.target.value;
    
    // Track typing speed
    if (typingStartTime.current === 0) {
      typingStartTime.current = now;
    }
    
    const timeDiff = now - lastTypingTime.current;
    if (timeDiff > 100) { // New typing session
      const characters = value.length;
      const timeSpent = now - typingStartTime.current;
      if (timeSpent > 0) {
        botDetectionService.trackTypingSpeed(characters, timeSpent);
      }
      typingStartTime.current = now;
    }
    
    lastTypingTime.current = now;
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
  };

  // Track copy-paste events
  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      botDetectionService.trackCopyPaste();
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, []);

  // Track form errors
  const trackError = () => {
    botDetectionService.trackError();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setBotDetected(false);

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError(t('auth.passwordsDoNotMatch'));
      trackError();
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError(t('auth.passwordTooShort'));
      trackError();
      setLoading(false);
      return;
    }

    try {
      // If bot detection wrapper is provided, use it; otherwise run detection directly
      if (onSubmit) {
        // Bot detection wrapper will handle detection and call onHumanVerified when it passes
        // We'll proceed with registration after that callback
        await onSubmit(formData);
        // If we get here, bot detection passed (wrapper resolved without error)
      } else {
        // Perform bot detection directly
        const botResult = await botDetectionService.detectBot(formData);
        
        if (botResult.isBot) {
          setBotDetected(true);
          setError(t('auth.botDetected', { confidence: botResult.confidence }));
          setLoading(false);
          return;
        }
      }

      // Register user (after bot detection passes)
      await UserAuthService.getInstance().register(
        formData.email, 
        formData.password, 
        formData.displayName || undefined
      );
      
      onSuccess();
    } catch (err: any) {
      console.error('Registration failed:', err);
      // Handle bot detection errors
      if (err.message?.includes('Bot detected')) {
        setBotDetected(true);
        const confidenceMatch = err.message.match(/confidence: (\d+)%/);
        const confidence = confidenceMatch ? confidenceMatch[1] : 'unknown';
        setError(t('auth.botDetected', { confidence }));
      } else {
        setError(getCleanErrorMessage(err) || t('auth.registerError'));
      }
      trackError();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 4, maxWidth: 400, mx: 'auto' }}>
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {t('auth.createAccount')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('auth.registerSubtitle')}
        </Typography>
      </Box>

      {error && (
        <Alert 
          severity={botDetected ? "warning" : "error"} 
          sx={{ mb: 2 }}
        >
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          fullWidth
          label={t('auth.displayName')}
          value={formData.displayName}
          onChange={handleTyping('displayName')}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Person />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          fullWidth
          label={t('auth.email')}
          type="email"
          value={formData.email}
          onChange={handleTyping('email')}
          required
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Email />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          fullWidth
          label={t('auth.password')}
          type={showPassword ? 'text' : 'password'}
          value={formData.password}
          onChange={handleTyping('password')}
          required
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Lock />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <TextField
          fullWidth
          label={t('auth.confirmPassword')}
          type={showConfirmPassword ? 'text' : 'password'}
          value={formData.confirmPassword}
          onChange={handleTyping('confirmPassword')}
          required
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Lock />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  edge="end"
                >
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={loading}
          sx={{ mt: 2 }}
        >
          {loading ? t('common.loading') : t('auth.createAccount')}
        </Button>

        <Divider sx={{ my: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {t('auth.or')}
          </Typography>
        </Divider>

        <Button
          fullWidth
          variant="outlined"
          onClick={onSwitchToLogin}
          sx={{ mt: 1 }}
        >
          {t('auth.alreadyHaveAccount')}
        </Button>
      </Box>
    </Paper>
  );
};

export default RegisterForm;